// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

/**
 * Unified collection query hook with mode routing.
 *
 * Delegates to either local RxDB (useLocalAnyCollection) or remote D1
 * (useRemoteQueryConditional) based on the CollectionModeProvider context.
 *
 * When no provider is mounted, all collections route to local (backward compatible).
 */

import { useCollectionMode } from '../CollectionModeContext'
import { useLocalAnyCollection } from './useLocalAnyCollection'
import { useRemoteQueryConditional } from './useRemoteQuery'
import type { UseLocalAnyCollectionResult, AnyDb } from './useLocalAnyCollection'

export type UseAnyCollectionOptions = import('./useLocalAnyCollection').UseLocalAnyCollectionOptions

export type UseAnyCollectionResult<T> = UseLocalAnyCollectionResult<T>

/**
 * React hook that queries either a native RxDB collection or a virtual
 * schema-in-db collection transparently.
 *
 * Routes to local RxDB or remote D1 based on CollectionModeProvider context.
 * When no provider is mounted, all collections route to local.
 *
 * @param db - RxDatabase instance (or null/undefined)
 * @param collectionName - Collection name (native or virtual)
 * @param options - Query options: selector, sort, limit, skip, orgId
 * @returns Reactive data array with loading/error state
 */
export function useAnyCollection<T = Record<string, unknown>>(
  db: AnyDb | null | undefined,
  collectionName: string,
  options: UseAnyCollectionOptions = {}
): UseAnyCollectionResult<T> {
  const modeCtx = useCollectionMode()
  const mode = modeCtx?.getMode(collectionName) ?? 'local'
  const isRemote = mode === 'remote'

  // Both hooks are always called (rules of hooks) but only one is active.
  const localResult = useLocalAnyCollection<T>(
    isRemote ? null : db,
    collectionName,
    options
  )

  const remoteResult = useRemoteQueryConditional<T>(
    isRemote ? collectionName : null,
    {
      selector: options.selector,
      sort: options.sort,
      limit: options.limit,
      skip: options.skip,
    }
  )

  return isRemote ? remoteResult : localResult
}
